import { useEffect, useState } from 'react';
import { useAdminContext } from '../../../api/AdminContext';
import useQuestionBankContext from '../../../api/QuestionBankContext';
import { useLoadingContext } from '../../../context/LoadingContext';
import { type QuestionBankID } from '../../../data/interface-question-bank';
import CurrentBankNotice from './CurrentBankNotice';
import QuestionList from './questions-summary-list/QuestionList';
import SearchAndFilter from './questions-summary-list/search-and-filter/SearchAndFilter';
import MainStatsDisplay from './questions-summary-list/stats/MainStatsDisplay';
import QuestionsManagerContainer from './QuestionsManagerContainer';
import type { Question } from '../../../data/interface-question';

const QuestionsManagerDashboard = () => {
	const { setLoading, setLoadingMessage } = useLoadingContext();
	const { getQuestionsForAdmin } = useAdminContext();
	const { categories, getCategories } = useQuestionBankContext();
	const [bankQuestions, setBankQuestions] = useState<Question[]>([]);
	const [filteredQuestions, setFilteredQuestions] = useState<Question[]>([]);
	const [showNoResultsMSG, setShowNoResultsMSG] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const lsBankID = localStorage.getItem('questionBankID') as QuestionBankID;
	const [questionBankID, setQuestionBankID] = useState<QuestionBankID>(
		lsBankID !== null ? lsBankID : 'enurm'
	);

	// DEVELOPMENT STEPS
	// 1. Check localStorage for exam-id, if found, load the exam else load 'enurm' exam
	// 2. Load questions for the exam
	// 3. Load counters

	const handleQBankChange = (v: QuestionBankID) => {
		setQuestionBankID(v);
		setError(null); // Clear any previous errors
	};

	const handleBankChange = (v: QuestionBankID) => handleQBankChange(v);

	const resetQuestions = () => {
		setFilteredQuestions([]);
	};

	// Load questions when questionBankID changes
	useEffect(() => {
		const loadQuestions = async () => {
			setLoading(true);
			setLoadingMessage('Loading questions...');
			setError(null);
			try {
				const questions = await getQuestionsForAdmin(questionBankID);
				if (questions.length === 0) {
					setError('No questions found for this bank');
				}
				setBankQuestions(questions);
				setFilteredQuestions(questions);
			} catch (error) {
				console.error('Error loading questions:', error);
				setError('Failed to load questions. Please try again.');
			} finally {
				setLoading(false);
			}
		};

		void loadQuestions();
	}, [questionBankID, getQuestionsForAdmin, setLoading, setLoadingMessage]);

	// Load categories only once on mount
	useEffect(() => {
		void getCategories();
		localStorage.setItem('questionBankID', 'enurm');
	}, []); // Empty dependency array since we only want to load categories once

	const renderContent = () => {
		if (error !== null) {
			return (
				<div className='w-100 mb-4'>
					<p className='text-red-500'>{error}</p>
				</div>
			);
		}

		return (
			<>
				<SearchAndFilter
					questions={bankQuestions}
					questionBankID={questionBankID}
					resetQuestions={resetQuestions}
					setFilteredQuestions={setFilteredQuestions}
					setShowNoResultsMSG={setShowNoResultsMSG}
				/>
				{showNoResultsMSG ? (
					<div className='w-100'>
						<p className='text-red-500'>
							No results found. Please try a different search term.
						</p>
					</div>
				) : (
					<QuestionList
						questionBankID={questionBankID}
						questions={bankQuestions}
						filteredQuestions={filteredQuestions}
						categories={
							categories?.filter((c) => c.questionBank === questionBankID) ?? []
						}
					/>
				)}
			</>
		);
	};

	return (
		<QuestionsManagerContainer title='Questions Manager' bgColor='bg-brand-25'>
			<div className='my-8 w-full'>
				<div className='px-2 sm:px-4 lg:px-0'>
					<CurrentBankNotice
						examID={questionBankID}
						handleQBankChange={handleBankChange}
						showAddQuestionBtn={true}
					/>
					<MainStatsDisplay examID={questionBankID} questions={bankQuestions} />
					<hr className='w-3/4 mx-auto my-8' />
					{renderContent()}
				</div>
			</div>
		</QuestionsManagerContainer>
	);
};
export default QuestionsManagerDashboard;
